import './styles/Accordion.scss';

import { CaretDown } from '@phosphor-icons/react';
import {
  Children,
  cloneElement,
  createElement,
  isValidElement,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks/useClassHelper';
import { PropsWithClassName } from '../../types/common';
import { ACCORDION_REVEAL_EVENT } from './reveal';

const componentName = 'c-Accordion';

const AccordionHeader = ({ children }: PropsWithChildren) => {
  return children;
};

const AccordionContent = ({
  children,
  className,
}: PropsWithChildren<PropsWithClassName>) => {
  if (className) {
    return createElement(
      'div',
      {
        className,
      },
      children,
    );
  }

  return children;
};

type HeaderContentType = {
  header?: ReactNode | undefined;
  content?: ReactNode | undefined;
};

interface AccordionProps extends PropsWithChildren {
  defaultOpen?: boolean;
  theme?: 'odd' | 'even' | 'default';
  variant?: 'default' | 'slim';
  iconOptions?: {
    size?: number;
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
    position?: 'start' | 'close-end' | 'end';
    color?: string;
  };
  classOverwrite?: string;
  disabled?: boolean;
  dataToggleTestId?: string;
  withFixedHeader?: boolean;
  noPaddingOnTitle?: boolean;
  className?: string;
  showActiveItemBgColor?: boolean;
  /** Adds a gray hover background on the header row. */
  withHoverEffect?: boolean;
  // Controlled Accordion
  handleOpen?: () => void;
  isOpen?: boolean;
  arrowReverse?: boolean;
  /** Triggers the accordion to close when its value changes. */
  collapseSignal?: any;
  actionContent?: ReactNode;
  wrapperClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

const Accordion = ({
  children,
  defaultOpen = false,
  theme = 'default',
  variant = 'default',
  iconOptions = {
    size: 24,
    weight: 'fill',
    position: 'start',
  },
  classOverwrite = 'c-Accordion',
  className = '',
  showActiveItemBgColor = true,
  withHoverEffect = true,
  disabled = false,
  dataToggleTestId,
  withFixedHeader = true,
  noPaddingOnTitle,
  // Controlled Accordion
  handleOpen,
  isOpen,
  arrowReverse,
  collapseSignal,
  actionContent,
  wrapperClassName = '',
  triggerClassName = '',
  contentClassName = '',
}: AccordionProps) => {
  const randomId = Math.random().toString(36).substring(7);
  const c = useClassHelper(componentName);
  const overwriteClass = useClassHelper(classOverwrite);

  const [open, setOpen] = useState(defaultOpen);

  const [{ header, content }, setContent] = useState<HeaderContentType>({});

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      return;
    }

    if (open !== defaultOpen) {
      setOpen(defaultOpen);
    }
  }, [defaultOpen, disabled]);

  useEffect(() => {
    if (collapseSignal) {
      setOpen(false);
    }
  }, [collapseSignal]);

  const rootRef = useRef<HTMLDivElement>(null);
  const revealState = useRef({ disabled, isOpen, handleOpen, open });

  useEffect(() => {
    revealState.current = { disabled, isOpen, handleOpen, open };
  });

  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    const onReveal = () => {
      const current = revealState.current;

      if (current.disabled) {
        return;
      }

      if (current.isOpen !== undefined) {
        if (!current.isOpen) {
          current.handleOpen?.();
        }
        return;
      }

      if (!current.open) {
        setOpen(true);
      }
    };

    root.addEventListener(ACCORDION_REVEAL_EVENT, onReveal);

    return () => {
      root.removeEventListener(ACCORDION_REVEAL_EVENT, onReveal);
    };
  }, []);

  useLayoutEffect(() => {
    const childrenArray = Children.toArray(children);

    const header = childrenArray.find(child => {
      return isValidElement(child) && child.type === AccordionHeader;
    });

    const content = childrenArray.find(child => {
      return isValidElement(child) && child.type === AccordionContent;
    });

    setContent({ header, content });
  }, [children]);

  const toggleOpen = () => {
    if (disabled) {
      return;
    }

    setOpen(!open);
  };

  const showStartIcon =
    iconOptions.position === undefined ||
    (iconOptions.position && iconOptions.position === 'start');
  const showEndIcon = iconOptions.position && iconOptions.position === 'end';
  const showCloseEndIcon =
    iconOptions.position && iconOptions.position === 'close-end';

  const getStateClass = () => {
    return isOpen || open ? '' : '__content-animator--closed';
  };

  const caretIcon = (
    <span
      className={c(
        '__arrow',
        isOpen || open ? '__arrow--open' : '',
        showEndIcon ? '__arrow--end' : '',
        showCloseEndIcon ? '__arrow--close-end' : '',
      )}
    >
      <CaretDown
        size={iconOptions.size}
        color={iconOptions.color}
        aria-label={`${isOpen || open ? 'close' : 'open'} accordion icon`}
      />
    </span>
  );

  const accordionClass =
    c('__header', '__header__toggle', `__header--${theme}`, {
      '__header--disabled': disabled,
      '__header--fixed-height': withFixedHeader,
      '__arrow-reverse': arrowReverse,
      '__no-padding': noPaddingOnTitle,
    }) +
    ' ' +
    overwriteClass('__header', '__header__toggle', `__header--${theme}`) +
    ' ' +
    triggerClassName;

  return (
    <div
      ref={rootRef}
      className={cn(
        c(
          'base',
          {
            __active: isOpen ?? open,
            __disabled: disabled,
          },
          `--variant-${variant}`,
        ),
        {
          'shadow-xs border border-gray-200 rounded-[8px] hover:bg-gray-50':
            variant === 'slim',
        },
        wrapperClassName,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between',
          {
            'hover:bg-gray-100 transition-colors': withHoverEffect,
            'bg-gray-100': showActiveItemBgColor && (isOpen ?? open),
          },
          className,
        )}
      >
        <button
          type="button"
          data-testid={dataToggleTestId || 'accordion-toggle'}
          aria-pressed={!open}
          aria-label="expand row"
          className={cn(accordionClass)}
          onClick={handleOpen ?? toggleOpen}
          aria-expanded={isOpen || open ? 'true' : 'false'}
          tabIndex={0}
          aria-controls={`accordion-${randomId}`}
          data-cy={dataToggleTestId || `accordion-toggle-${randomId}`}
        >
          {showStartIcon && caretIcon}
          {header}
          {showEndIcon || showCloseEndIcon ? caretIcon : null}
        </button>
        {actionContent}
      </div>
      {/*
        The panel is wrapped rather than hidden: the animator is a one-row grid
        going from 1fr to 0fr, which interpolates where `height: auto` and
        `display: none` cannot. Everything that styles the panel — padding,
        `contentClassName`, the variant rules — stays on the inner element, so
        it is clipped along with the content instead of leaving a strip behind
        while collapsed.
      */}
      <div className={c('__content-animator', getStateClass())}>
        {/*
          The clipper is the grid item and must stay padding-free: a grid item
          can never shrink below its own padding, so any padding here — or on a
          padded panel made the item directly, e.g. a consumer's `!p-6` — puts a
          floor under the 0fr track and the closed accordion keeps that height.
          Measured in Chromium: 48px of leftover height without it.
        */}
        <div className={c('__content-clipper')}>
          <div
            role="region"
            id={randomId}
            aria-labelledby={`accordion-${randomId}`}
            aria-hidden={isOpen || open ? 'false' : 'true'}
            className={cn(c('__content', contentClassName), {
              'bg-white! rounded-bl-[8px] rounded-br-[8px]': variant === 'slim',
            })}
          >
            {content}
          </div>
        </div>
      </div>
    </div>
  );
};

Accordion.Header = AccordionHeader;
Accordion.Content = AccordionContent;

export default Accordion;
